import { Inject, Injectable } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { environment } from 'environments/environment';
import moment from 'moment';
import { timer } from 'rxjs';
import { filter, tap } from 'rxjs/operators';

import { UpdateModalComponent } from '../components/update-modal/update-modal.component';
import { setupModal, TypedNgbModal } from '../utils/typed-modal';

interface Feature {
  id: string;
  month: string;
  description: string;
}

interface AppData {
  version?: string;
  features?: Feature[];
}

const PREVIOUS_FEATURE_KEY = 'updates.previousFeature';
const CURRENT_VERSION_KEY = 'updates.currentVersion';

@Injectable({ providedIn: 'root' })
export class CheckForUpdateService {
  private _currentAppData?: AppData;
  private _latestAppData?: AppData;

  private _previousFeature?: string;
  get previousFeature() {
    if (this._previousFeature == null) {
      this._previousFeature = localStorage.getItem(PREVIOUS_FEATURE_KEY) || '';
    }
    return this._previousFeature;
  }

  private _version?: string;
  get version() {
    if (this._version == null) {
      this._version = localStorage.getItem(CURRENT_VERSION_KEY) || '';
    }
    return this._version;
  }

  get isVersionPending() {
    return this._latestAppData != null;
  }

  get newFeatureCount() {
    const currentFeatures = this._currentAppData?.features || [];

    let count = 0;
    for (const feature of currentFeatures) {
      if (feature.id === this.previousFeature) {
        break;
      }
      count += 1;
    }

    return count;
  }

  constructor(
    private updates: SwUpdate,
    @Inject(NgbModal) private ngbModal: TypedNgbModal,
  ) {}

  init() {
    // App was never becoming stable (appRef.isStable) so wait 5 seconds instead
    timer(5 * 1000, environment.versionCheckInterval).subscribe(() =>
      this.updates.checkForUpdate(),
    );

    let updateModal: UpdateModalComponent | undefined;
    this.updates.versionUpdates
      .pipe(
        tap(event => {
          switch (event.type) {
            case 'VERSION_READY':
              this._currentAppData = event.currentVersion?.appData;
              this._latestAppData = event.latestVersion?.appData;
              this.setVersion(this._currentAppData?.version, false);
              break;
            case 'NO_NEW_VERSION_DETECTED':
              if (!this._latestAppData) {
                this._currentAppData = event.version?.appData;
                this.setVersion(this._currentAppData?.version, false);
              }
              break;
          }
        }),
        filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'),
      )
      .subscribe(event => {
        const features = this.getFeatures(event);
        if (!updateModal) {
          const modalRef = setupModal(
            this.ngbModal.open(UpdateModalComponent),
            modal => {
              modal.features = features;
            },
            modal => [modal.update.subscribe(() => this.updateVersion())],
          );
          updateModal = modalRef.componentInstance;
          modalRef.result.then(
            () => (updateModal = undefined),
            () => (updateModal = undefined),
          );
        } else {
          updateModal.features = features;
        }
      });
  }

  private setVersion(version: string | undefined, isUpdate: boolean) {
    if (version === '<VERSION>') {
      version = moment().format('YYYYMMDD') + '.';
      if (this.version.indexOf(version) === 0) {
        if (!isUpdate) {
          return;
        }
        version = localStorage
          .getItem(CURRENT_VERSION_KEY)
          ?.replace(/\.(\d+)/, (_, n) => '.' + (+n + 1));
      } else {
        version += '0';
      }
    }

    if (!isUpdate) {
      this._version = version;
    }

    try {
      if (version) {
        localStorage.setItem(CURRENT_VERSION_KEY, version);
      } else {
        localStorage.removeItem(CURRENT_VERSION_KEY);
      }
    } catch (err) {
      environment.warn(err);
    }
  }

  updateVersion() {
    this.setVersion(this._latestAppData?.version, true);
    this.storePreviousFeature();
    location.reload();
    // Need to remove hashs
    // location.href = location.href.split(/#/)[0];
  }

  private storePreviousFeature() {
    try {
      localStorage.setItem(PREVIOUS_FEATURE_KEY, this._currentAppData?.features?.[0]?.id || '');
    } catch (err) {
      environment.warn(err);
    }
  }

  markAsRead() {
    this._previousFeature = this._currentAppData?.features?.[0]?.id || '';
    this.storePreviousFeature();
  }

  getFeatures({
    currentVersion,
    latestVersion,
  }: {
    currentVersion?: {
      appData?: AppData;
    };
    latestVersion: {
      appData?: AppData;
    };
  }) {
    function getFeatures(appData?: AppData) {
      return appData?.features || [];
    }
    const currentFeatures = getFeatures(currentVersion?.appData);
    const currentIds = new Set<string>(currentFeatures.map(f => f.id));
    const latestFeatures = getFeatures(latestVersion.appData);
    const newFeatures = latestFeatures
      .filter(f => !currentIds.has(f.id))
      .map(f => f.description)
      .reverse();

    if (newFeatures.length === 0) {
      return environment.defaultFeatures;
    }

    return newFeatures;
  }

  getAllFeaturesByMonth() {
    if (!this._currentAppData?.features) {
      return [];
    }

    const byMonth: Record<
      string,
      Array<{ id: string; description: string; status: 'OLD' | 'NEW' | 'PENDING' }>
    > = {};

    const currentFeatures = this._currentAppData?.features || [];
    const currentIds = new Set<string>(currentFeatures.map(f => f.id));
    const pendingFeatures = (this._latestAppData?.features || []).filter(
      f => !currentIds.has(f.id),
    );

    for (const feature of pendingFeatures) {
      if (!byMonth[feature.month]) {
        byMonth[feature.month] = [];
      }

      byMonth[feature.month].push({
        ...feature,
        status: 'PENDING',
      });
    }

    let featureIsNew = true;
    for (const feature of currentFeatures) {
      featureIsNew = featureIsNew && feature.id !== this.previousFeature;
      if (!byMonth[feature.month]) {
        byMonth[feature.month] = [];
      }

      byMonth[feature.month].push({ ...feature, status: featureIsNew ? 'NEW' : 'OLD' });
    }

    return Object.entries(byMonth).map(([month, features]) => ({
      month,
      features,
    }));
  }
}
